/**
 * Keycloak configuration for the Service Portal Shell.
 *
 * The shell reuses the legacy Service Portal webapp's public UI client so a
 * user authenticating against the shell is authenticated against the same
 * Keycloak realm and client the legacy portal uses server-side.
 *
 * The public client id and realm are identical across every environment (they
 * are hardcoded defaults in the legacy webapp's `KeycloakConfig`); only the
 * Keycloak base URL varies per environment. See
 * `service-portal/src/main/java/com/tomra/tomraplus/core/client/tcv2/auth/keycloak/KeycloakConfig.java`
 * and `service-portal/src/main/resources/keycloak/login-required.mustache`.
 */

/** Public UI client id used by the legacy webapp (`DEFAULT_KEYCLOAK_PUBLIC_CLIENT_ID`). */
export const KEYCLOAK_CLIENT_ID = 'tp-webapp-ui';

/** Keycloak realm used by the legacy webapp (`DEFAULT_KEYCLOAK_REALM`). */
export const KEYCLOAK_REALM = 'TomraConnectUsers';

export interface KeycloakConfig {
  url: string;
  realm: string;
  clientId: string;
}

/**
 * Maps each shell Firebase Hosting hostname to the Keycloak base URL of the
 * environment it belongs to. Mirrors the hostname list in `legacy-portal.ts`,
 * so the shell talks to the same identity provider as the legacy portal it
 * embeds.
 */
const KEYCLOAK_URL_BY_HOSTNAME: Record<string, string> = {
  'eu.lab.service-portal.tomra.cloud': 'https://id.eu.lab.tomra.cloud/auth',
  'verilab.service-portal.tomra.cloud': 'https://id.verilab.tomra.cloud/auth',
  'eu.test.service-portal.tomra.cloud': 'https://id.eu.test.tomra.cloud/auth',
  'us.test.service-portal.tomra.cloud': 'https://id.us.test.tomra.cloud/auth',
  'au.test.service-portal.tomra.cloud': 'https://id.au.test.tomra.cloud/auth',
  'eu1.prod.service-portal.tomra.cloud': 'https://id.eu.prod.tomra.cloud/auth',
  'eu2.prod.service-portal.tomra.cloud': 'https://id.eu.prod.tomra.cloud/auth',
  'us.prod.service-portal.tomra.cloud': 'https://id.us.prod.tomra.cloud/auth',
  'au.prod.service-portal.tomra.cloud': 'https://id.au.prod.tomra.cloud/auth'
};

/**
 * Resolves the Keycloak configuration to use, based on the hostname the shell
 * is currently being served from.
 *
 * Falls back to `VITE_KEYCLOAK_URL` (and optional `VITE_KEYCLOAK_REALM` /
 * `VITE_KEYCLOAK_CLIENT_ID`) for local development, where `hostname` is
 * typically `localhost`.
 *
 * Returns `undefined` if a production/test hostname is not recognized and no
 * local fallback is configured, so callers can surface a clear error instead
 * of authenticating against the wrong environment.
 */
export function resolveKeycloakConfig(hostname: string): KeycloakConfig | undefined {
  const realm = import.meta.env.VITE_KEYCLOAK_REALM || KEYCLOAK_REALM;
  const clientId = import.meta.env.VITE_KEYCLOAK_CLIENT_ID || KEYCLOAK_CLIENT_ID;

  if (hostname in KEYCLOAK_URL_BY_HOSTNAME) {
    return {
      url: KEYCLOAK_URL_BY_HOSTNAME[hostname],
      realm,
      clientId
    };
  }

  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    const url = import.meta.env.VITE_KEYCLOAK_URL;
    if (!url) {
      return undefined;
    }

    return {
      url,
      realm,
      clientId
    };
  }

  return undefined;
}
